import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from 'react-apollo';
import { Typography, CircularProgress, Box } from '@material-ui/core';

import { SessionContext } from '../../contexts/session';
import { GET_USER } from './queries';

const IMPERSONATION_READY = 'IMPERSONATION_READY';
const IMPERSONATION_PAYLOAD = 'IMPERSONATION_PAYLOAD';

const RETRY_INTERVAL_MS = 500;
const MAX_RETRIES = 20;

export const ImpersonationBridge: React.FC = () => {
  const history = useHistory();
  const { login, logout } = useContext(SessionContext);

  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Initializing...');

  // Clear any stale session to prevent unauthorized queries while bridge loads
  useEffect(() => {
    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryCountRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const receivedPayloadRef = useRef(false);
  const pendingTokenRef = useRef<string | null>(null);

  const [getUserQuery, { data: userData }] = useLazyQuery(GET_USER);

  // Step 3: Once user data arrives, reset store, login, redirect
  useEffect(() => {
    if (userData?.user && pendingTokenRef.current) {
      const token = pendingTokenRef.current;
      const user = userData.user;
      login(user, token);
      history.push('/taps');
    }
  }, [userData, login, history]);

  useEffect(() => {
    const studioOrigin = process.env.REACT_APP_STUDIO_ORIGIN;

    if (!studioOrigin) {
      setError('Configuration error: REACT_APP_STUDIO_ORIGIN is not set.');
      return;
    }

    const sendReady = () => {
      try {
        window.opener?.postMessage({ type: IMPERSONATION_READY }, studioOrigin);
      } catch {
        // postMessage can throw on cross-origin restrictions in some edge cases; ignore
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (receivedPayloadRef.current) return;

      // Strict origin validation
      if (event.origin !== studioOrigin) {
        return;
      }

      const data = event.data;

      if (!data || data.type !== IMPERSONATION_PAYLOAD) {
        return;
      }

      // Payload validation
      if (!data.token || !data.userId || !data.nonce) {
        setError('Invalid impersonation payload: missing required fields (token, userId, nonce).');
        cleanup();
        return;
      }

      receivedPayloadRef.current = true;
      cleanup();

      pendingTokenRef.current = data.token;
      setStatus('Authenticating...');

      getUserQuery({
        variables: { id: data.userId },
        context: { headers: { authorization: data.token } }
      });
    };

    const cleanup = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      window.removeEventListener('message', handleMessage);
    };

    window.addEventListener('message', handleMessage);

    // Start sending READY with bounded retries
    setStatus('Waiting for Studio...');
    sendReady();
    retryCountRef.current = 1;

    intervalRef.current = setInterval(() => {
      if (receivedPayloadRef.current) {
        cleanup();
        return;
      }

      retryCountRef.current += 1;

      if (retryCountRef.current > MAX_RETRIES) {
        cleanup();
        setError('Timed out waiting for impersonation payload from Studio.');
        return;
      }

      sendReady();
    }, RETRY_INTERVAL_MS);

    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
      {error ? (
        <Typography color="error" variant="body1">
          {error}
        </Typography>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: 16 }}>
            {status}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ImpersonationBridge;
